import React, { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { fetchCustomerQuotes, quoteSelector } from '../../redux/quoteSlice/quoteSlice'
import { rentalVehicleSelector } from '../../redux/rentalSlice/rentalVehicleSlice'
import { authSelector } from '../../redux/authSlice/authSlice'
import Header1 from '../../components/header/Header1'
import config from '../../config'

const MyQuotesPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { rentalDetails } = useAppSelector(rentalVehicleSelector)
  const { quotes, loading, error } = useAppSelector(quoteSelector)
  const { userId } = useAppSelector(authSelector)

  const statusMap: { [key: number]: string } = {
    0: '⏳ Pending',
    1: '✅ Approved',
    2: '❌ Rejected'
  }

  useEffect(() => {
    if (userId) dispatch(fetchCustomerQuotes(Number(userId)))
  }, [dispatch, userId])

  const getQuoteTimestamp = (q: any): number => {
    if (!q) return 0
    const rawDate =
      q.created_at ||
      q.createdAt ||
      q.creation_date ||
      q.creationDate ||
      q.create_time ||
      q.created_time ||
      q.createdTime ||
      q.request_date ||
      q.requestDate ||
      q.requested_at ||
      q.requestedAt ||
      q.timestamp ||
      q.date ||
      q.updated_at ||
      q.updatedAt ||
      q.rentalDetails?.created_at ||
      q.rentalDetails?.createdAt
    if (rawDate) {
      const time = new Date(rawDate).getTime()
      if (!isNaN(time) && time > 0) return time
    }
    return 0
  }

  const getQuoteNumericId = (q: any): number => {
    if (!q) return 0
    const candidates = [
      q.quote_id,
      q.quoteId,
      q.request_quote_id,
      q.requestQuoteId,
      q.request_id,
      q.requestId,
      q.quotation_id,
      q.quotationId,
      q.quotation_no,
      q.quotationNo,
      q.quote_number,
      q.quoteNumber,
      q.quote_no,
      q.quoteNo,
      q.rental_id,
      q.rentalId,
      q.booking_id,
      q.bookingId,
      q.rentalDetails?.quote_id,
      q.rentalDetails?.quoteId,
      q.rentalDetails?.id,
      q.id
    ]

    for (const val of candidates) {
      const num = Number(val)
      if (!isNaN(num) && num > 0) {
        return num
      }
    }
    return 0
  }

  const getQuoteId = (q: any, fallbackIndex?: number): string | number => {
    if (!q) return fallbackIndex !== undefined ? fallbackIndex : 1

    const candidates = [
      q.quote_id,
      q.quoteId,
      q.request_quote_id,
      q.requestQuoteId,
      q.request_id,
      q.requestId,
      q.quotation_id,
      q.quotationId,
      q.quotation_no,
      q.quotationNo,
      q.quote_number,
      q.quoteNumber,
      q.quote_no,
      q.quoteNo,
      q.rental_id,
      q.rentalId,
      q.booking_id,
      q.bookingId,
      q.rentalDetails?.quote_id,
      q.rentalDetails?.quoteId,
      q.rentalDetails?.id,
      q.id
    ]

    for (const val of candidates) {
      if (val !== undefined && val !== null && val !== '' && val !== 0 && val !== '0') {
        return val
      }
    }

    if (q._originalIndex !== undefined) {
      return q._originalIndex + 1
    }

    return fallbackIndex !== undefined ? fallbackIndex : 1
  }

  const sortedQuotes = useMemo(() => {
    if (!Array.isArray(quotes)) return []

    const withIndex = quotes.map((q: any, idx: number) => ({
      ...q,
      _originalIndex: idx
    }))

    return withIndex.sort((a: any, b: any) => {
      // 1. Compare creation timestamp (latest first)
      const timeA = getQuoteTimestamp(a)
      const timeB = getQuoteTimestamp(b)
      if (timeA > 0 && timeB > 0 && timeA !== timeB) {
        return timeB - timeA
      }

      // 2. Compare numeric ID (highest ID = newest quote first)
      const idA = getQuoteNumericId(a)
      const idB = getQuoteNumericId(b)
      if (idA > 0 && idB > 0 && idA !== idB) {
        return idB - idA
      }

      // If one has timestamp and the other doesn't
      if (timeA > 0 && timeB === 0) return -1
      if (timeB > 0 && timeA === 0) return 1

      // If one has valid numeric ID and the other doesn't
      if (idA > 0 && idB === 0) return -1
      if (idB > 0 && idA === 0) return 1

      // 3. Fallback: newer quotes are at the end of original list, so reverse order
      return b._originalIndex - a._originalIndex
    })
  }, [quotes])

  return (
    <div className="min-h-screen bg-gray-50">
      <Header1 title="My Quotes" to="/rentVehicle" />

      <div className="p-6 max-w-6xl mx-auto">
        {loading && <p className="text-lg text-gray-600">Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {sortedQuotes.length === 0 ? (
          <p className="text-gray-500 text-lg text-center mt-20">
            No quotes found.
          </p>
        ) : (
          <div className="grid gap-8">
            {sortedQuotes.map((q: any, index: number) => {
              const quoteId = getQuoteId(q, index + 1)
              const cardKey =
                q.quote_id ||
                q.quoteId ||
                q.request_quote_id ||
                (q.id && q.id !== 0 ? q.id : null) ||
                q._originalIndex ||
                index

              const requestedDate =
                q.created_at ||
                q.createdAt ||
                q.creation_date ||
                q.creationDate ||
                q.request_date ||
                q.requestDate

              return (
                <div
                  key={cardKey}
                  className="border rounded-2xl p-6 bg-white shadow-lg hover:shadow-xl transition-shadow"
                >
                  {/* Header */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h2 className="font-bold text-xl text-gray-800">
                        Quote #{quoteId}
                      </h2>
                      {requestedDate && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Requested on:{' '}
                          {new Date(requestedDate).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        q.status === 1
                          ? 'bg-green-100 text-green-700'
                          : q.status === 2
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {statusMap[q.status] || 'Unknown'}
                    </span>
                  </div>

                  {/* Dates */}
                  <div className="text-sm text-gray-600 mb-4">
                    <p>
                      <span className="font-semibold">From:</span>{' '}
                      {q.startDate
                        ? new Date(q.startDate).toLocaleDateString()
                        : 'N/A'}
                    </p>
                    <p>
                      <span className="font-semibold">To:</span>{' '}
                      {q.endDate
                        ? new Date(q.endDate).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>

                  {/* Address */}
                  <p className="font-medium text-gray-700 mb-4">
                    📍 Delivery to:{' '}
                    <span className="text-gray-900">{q.address}</span>
                  </p>

                  {/* Vehicles */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    {Array.isArray(q.vehicles) &&
                      q.vehicles.map((v: any, vIndex: number) => (
                        <div
                          key={vIndex}
                          className="flex flex-col items-center border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition"
                        >
                          <img
                            src={`${config.imageBaseURL}/${v.details?.image_url}`}
                            alt={v.details?.model_name}
                            className="w-full h-24 object-contain bg-white rounded mb-2"
                          />
                          <p className="text-sm font-semibold text-gray-800 text-center truncate w-full">
                            {v.details?.model_name} (x{v.count})
                          </p>
                          <p className="text-xs text-gray-500">
                            ₹{v.details?.rental_cost_per_day}/day
                          </p>
                          <p className="text-xs text-gray-600 font-medium">
                            Subtotal: ₹
                            {(
                              (q.rentalCost || 0) + (q.transportCost || 0)
                            ).toLocaleString()}
                          </p>
                        </div>
                      ))}
                  </div>

                  {/* Cost breakdown */}
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Rental Cost</span>
                      <span className="font-medium">
                        ₹{(q.rentalCost || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Transport Cost</span>
                      <span className="font-medium">
                        ₹{(q.transportCost || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <span>Operator Cost (Discounted)</span>
                      <span className="line-through">
                        ₹{(q.driverCost || 0).toLocaleString()}
                      </span>
                    </div>
                    <hr />
                    <div className="flex justify-between font-bold text-green-700 text-xl">
                      <span>Total</span>
                      <span>₹{(q.totalCost || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyQuotesPage
